import {ACCESS_LABELS} from './constants'
import type {ShareAccess, ShareActivityItem} from './types'

const parse = (iso: string | undefined): Date | null => {
  if (!iso) return null
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? null : date
}

/** "7 Oct 2026", or an em dash when the date is missing. */
export function formatShareDate(iso: string | undefined): string {
  const date = parse(iso)
  return date
    ? date.toLocaleDateString(undefined, {year: 'numeric', month: 'short', day: 'numeric'})
    : '—'
}

/** "7 Oct 2026, 14:05". */
export function formatShareDateTime(iso: string | undefined): string {
  const date = parse(iso)
  return date
    ? date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—'
}

export function isPast(iso: string | undefined, now: Date = new Date()): boolean {
  const date = parse(iso)
  return !!date && date.getTime() < now.getTime()
}

export const accessLabel = (access: ShareAccess | undefined): string =>
  access ? ACCESS_LABELS[access] ?? access : '—'

/** "Shared with 3 people", on the owner's shared folder (D9). */
export function sharedWithLabel(memberCount: number): string {
  if (memberCount <= 0) {
    return 'Shared, no members yet'
  }
  return memberCount === 1 ? 'Shared with 1 person' : `Shared with ${memberCount} people`
}

const asString = (value: unknown): string => (typeof value === 'string' ? value : '')

const asStrings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : []

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

/** "uploaded results/a.txt", from an ACTIVITY row's action and detail (`files_api`, `transfers`). */
export function describeActivity(item: ShareActivityItem): string {
  const detail = item.detail ?? {}
  const path = asString(detail.path)
  const sources = asStrings(detail.sources)
  const objects = typeof detail.objects === 'number' ? detail.objects : undefined
  const what = (fallback: string) =>
    sources.length === 1
      ? sources[0]
      : sources.length > 1
      ? plural(sources.length, 'item', 'items')
      : fallback
  switch (item.action) {
    case 'file.upload':
      return `uploaded ${path || 'a file'}`
    case 'folder.created':
      return `created the folder ${path || ''}`.trim()
    case 'files.deleted': {
      const paths = asStrings(detail.paths)
      return paths.length === 1
        ? `deleted ${paths[0]}`
        : `deleted ${plural(objects ?? paths.length, 'item', 'items')}`
    }
    case 'files.move':
      return `moved ${what('files')}${detail.status === 'failed' ? ' (failed)' : ''}`
    case 'files.copy':
      return `copied ${what('files')}${detail.status === 'failed' ? ' (failed)' : ''}`
    default:
      return item.action.replace(/[._]/g, ' ')
  }
}
